import React, { useCallback, useEffect, useMemo, useState } from "react";
import Joyride from "react-joyride";
import { useSelector } from "react-redux";
import { isAuthorized } from "components/Authorize/authorizeUtil";
import { useGetCurrentTenantConfigQuery } from "features/me/meSlice";
import { selectUserEmail } from "../../features/user/userSlice";
import { sideNavTourStyles } from "./SideNavTourStyle";
import { sideNavTourDescriptions } from "./sideNavTourDescriptions";

const isFeatureVisible = (item, currentTenantConfig) => {
    const featureFlagName = item?.featureFlagName || "";
    const featureFlagNameList = item?.featureFlagNameList || [];

    let featureAllowed =
        featureFlagName === "" &&
        (featureFlagNameList?.length === 0 || featureFlagNameList?.some((flag) => !flag));

    featureAllowed =
        featureAllowed ||
        featureFlagNameList.some(
            (flag) => currentTenantConfig?.tenantFeatureFlags?.[flag] === true
        );

    featureAllowed =
        featureAllowed ||
        currentTenantConfig?.tenantFeatureFlags?.[featureFlagName] === true;

    return featureAllowed;
};

const isMenuItemVisibleForTour = (item, currentTenantConfig) => {
    if (!item) return false;
    if (item.checkVisibility && !item.checkVisibility()) return false;
    if (item.profile && !isAuthorized(item.profile)) return false;
    if (!isFeatureVisible(item, currentTenantConfig)) return false;
    return true;
};

export default function SideNavTour({ topItems }) {
    const userEmail = useSelector(selectUserEmail);
    const [runTour, setRunTour] = useState(false);

    const { data: currentTenantConfig } = useGetCurrentTenantConfigQuery();

    const tourStorageKey = useMemo(() => {
        return userEmail ? `sideNavTourSeen(${userEmail})` : null;
    }, [userEmail]);

    const visibleTopItems = useMemo(() => {
        return (topItems || []).filter((item) =>
            isMenuItemVisibleForTour(item, currentTenantConfig)
        );
    }, [topItems, currentTenantConfig]);

    const tourSteps = useMemo(() => {
        const getVisibleChildren = (items) => {
            return (items || []).filter((child) =>
                isMenuItemVisibleForTour(child, currentTenantConfig)
            );
        };

        const renderChildrenList = (items, level = 0) => {
            const visibleItems = getVisibleChildren(items);
            if (!visibleItems.length) return null;

            return (
                <ul
                    style={{
                        margin: level === 0 ? "8px 0 0 18px" : "4px 0 0 18px",
                        padding: 0,
                        textAlign: "left",
                    }}
                >
                    {visibleItems.map((child) => {
                        const visibleGrandchildren = getVisibleChildren(child.children || []);

                        return (
                            <li key={child.key} style={{ marginBottom: 4 }}>
                                <div>{child.text}</div>
                                {visibleGrandchildren.length > 0
                                    ? renderChildrenList(visibleGrandchildren, level + 1)
                                    : null}
                            </li>
                        );
                    })}
                </ul>
            );
        };

        const buildDescription = (item) => {
            const base = sideNavTourDescriptions[item.key];
            if (!base) return null;

            const visibleChildren = getVisibleChildren(item.children || []);

            if (!visibleChildren.length) {
                return <div>{base}</div>;
            }

            return (
                <div>
                    <div>{base}</div>
                    <div style={{ marginTop: 10, fontWeight: 600 }}>
                        Available in this section:
                    </div>
                    {renderChildrenList(visibleChildren)}
                </div>
            );
        };

        return [
            {
                target: ".tour-rail-start-here",
                placement: "right",
                disableBeacon: true,
                content: (
                    <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
                            Navigation Tour
                        </div>
                        <div style={{ fontSize: 14 }}>
                            This quick tour will guide you through the main sections of the application.
                        </div>
                    </div>
                ),
            },

            ...visibleTopItems
                .map((item) => {
                    const content = buildDescription(item);
                    if (!content) return null;

                    return {
                        target: `.tour-rail-${item.key}`,
                        content,
                        disableBeacon: true,
                        placement: "right",
                    };
                })
                .filter(Boolean),
        ];
    }, [visibleTopItems, currentTenantConfig]);

    useEffect(() => {
        if (!tourStorageKey) return;

        const seen = localStorage.getItem(tourStorageKey);
        if (!seen && tourSteps.length > 0) {
            setRunTour(true);
        }
    }, [tourSteps.length, tourStorageKey]);

    const handleJoyrideCallback = useCallback((data) => {
        if (data.status === "finished" || data.status === "skipped") {
            if (tourStorageKey) {
                localStorage.setItem(tourStorageKey, "true");
            }
            setRunTour(false);
        }
    }, [tourStorageKey]);

    return (
        <Joyride
            steps={tourSteps}
            run={runTour}
            continuous
            showSkipButton
            showProgress
            disableScrolling={true}
            scrollToFirstStep
            callback={handleJoyrideCallback}
            styles={sideNavTourStyles}
        />
    );
}